import React, { useMemo, useEffect, useState } from "react";
import {
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  Box,
  InputAdornment,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import {
  useSpecificationStateStore,
  getItemMetadata,
  type ChangeTarget,
  type Change,
} from "../features/conditions/specificationState";
import {
  useValidationStateStore,
  getAttributeConstraints,
  getParameterConstraints,
  getRangeViolationMessage,
} from "../features/conditions/validationState";
import { BooleanToggle } from "./BooleanToggle";
import {
  formatValueType,
  getEnumValuesFromConfig,
  isEnumDataType,
  isMultiSelectEnumDataType,
  isPartNumberValue,
  isRangeValue,
  parseMultiEnumValue,
  serializeMultiEnum,
} from "../utils/valueType";
import { findConstraintOfType, isFieldLocked } from "../utils/conditions";

// Shared between AttributeInput and ParameterInput. Pulls the numeric
// min/max bounds out of an `attribute_range` / `parameter_range` constraint.
// Returns null for any other constraint type (or null input), which both
// inputs use as a "no range constraint to enforce" signal.
const getRangeConstraintBounds = (
  constraint: TemplateConstraintModel | null | undefined
): { min?: number; max?: number } | null => {
  if (!constraint) return null;
  if (
    constraint.constraint_type !== "attribute_range" &&
    constraint.constraint_type !== "parameter_range"
  ) {
    return null;
  }
  const ranged = constraint as
    | AttributeRangeConstraint
    | ParameterRangeConstraint;
  return {
    min: ranged.config.min_value,
    max: ranged.config.max_value,
  };
};

// Shared validation helper — local UI state only.
// Store-level violations are managed by runClientSideValidations.
const validateRangeConstraint = (
  currentValue: string | number | boolean | null | undefined,
  rangeConstraint: { min?: number; max?: number },
  setViolatesRange: (violates: boolean) => void,
  setValidationMessage: (message: string | null) => void,
  constraintTypeString: "attribute_range" | "parameter_range",
  fieldName: string
) => {
  if (!rangeConstraint) {
    return;
  }

  const normalizedValue =
    typeof currentValue === "string" ? currentValue.trim() : currentValue;

  if (
    normalizedValue === "" ||
    normalizedValue === null ||
    normalizedValue === undefined
  ) {
    setViolatesRange(false);
    setValidationMessage(null);
    return;
  }

  let numValue: number | null = null;

  if (typeof normalizedValue === "number") {
    numValue = normalizedValue;
  } else if (typeof normalizedValue === "string") {
    const parsed = Number(normalizedValue);
    if (Number.isFinite(parsed)) {
      numValue = parsed;
    }
  }

  if (numValue === null) {
    setViolatesRange(false);
    setValidationMessage(null);
    return;
  }

  let violates = false;
  const { min, max } = rangeConstraint;
  if (min !== undefined && numValue < min) violates = true;
  if (max !== undefined && numValue > max) violates = true;

  const fieldKind =
    constraintTypeString === "attribute_range" ? "Attribute" : "Parameter";
  const message = violates
    ? getRangeViolationMessage(min, max, fieldName, fieldKind)
    : null;

  setViolatesRange(violates);
  setValidationMessage(message);
};

type ValueEditorProps = {
  currentValue: ValueType | null | undefined;
  isChanged: boolean;
  isLocked: boolean;
  disabled?: boolean;
  enumValues: string[] | null;
  /** Render enum options as a multi-select (MULTISELECT_ENUM). */
  isMultiSelect?: boolean;
  rangeConstraint: { min?: number; max?: number } | null;
  violatesRange: boolean;
  validationMessage: string | null;
  units?: string | null;
  onChange: (nextValue: ValueType) => void;
  /**
   * The field's type-level default, if any. Shown as a hint in empty inputs
   * to prompt the user to enter it; never written into the input itself.
   */
  placeholderValue?: ValueType | null;
};

// Build placeholder hints for each editor variant from an optional default.
// Placeholders only render when the input is empty.
const getPlaceholders = (
  placeholderValue: ValueType | null | undefined
): { text: string; min: string; max: string; select: string } => {
  if (isRangeValue(placeholderValue)) {
    const formatted = formatValueType(placeholderValue);
    return {
      text: formatted ? `Default: ${formatted}` : "Enter value",
      min: Number.isFinite(placeholderValue.min_value)
        ? `Min: ${placeholderValue.min_value}`
        : "Min",
      max: Number.isFinite(placeholderValue.max_value)
        ? `Max: ${placeholderValue.max_value}`
        : "Max",
      select: formatted ? `Default: ${formatted}` : "Select value",
    };
  }
  const formatted = isPartNumberValue(placeholderValue)
    ? placeholderValue.part_number
    : formatValueType(placeholderValue);
  return {
    text: formatted ? `Default: ${formatted}` : "Enter value",
    min: "Min",
    max: "Max",
    select: formatted ? `Default: ${formatted}` : "Select value",
  };
};

const ValueEditor: React.FC<ValueEditorProps> = ({
  currentValue,
  isChanged,
  isLocked,
  disabled = false,
  enumValues,
  isMultiSelect = false,
  rangeConstraint,
  violatesRange,
  validationMessage,
  units,
  onChange,
  placeholderValue,
}) => {
  const placeholders = getPlaceholders(placeholderValue);

  if (typeof currentValue === "boolean") {
    const isDisabled = disabled || isLocked;
    return (
      <BooleanToggle
        value={Boolean(currentValue)}
        onChange={(nextValue) => onChange(nextValue)}
        disabled={isDisabled}
        isChanged={isChanged}
      />
    );
  }

  if (isLocked) {
    return (
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 400,
          color: "#000",
          textAlign: "right",
        }}
      >
        {formatValueType(currentValue as ValueType)}
      </Typography>
    );
  }

  if (isRangeValue(currentValue)) {
    const minValue = Number.isFinite(currentValue.min_value)
      ? currentValue.min_value
      : "";
    const maxValue = Number.isFinite(currentValue.max_value)
      ? currentValue.max_value
      : "";
    return (
      <FormControl
        fullWidth
        sx={{ display: "flex", flexDirection: "column", flex: 1 }}
      >
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <TextField
            size="small"
            variant="outlined"
            value={minValue}
            onChange={(e) => {
              const nextMin =
                e.target.value === "" ? null : Number(e.target.value);
              onChange({
                min_value:
                  nextMin === null || Number.isNaN(nextMin)
                    ? Number.NaN
                    : nextMin,
                max_value: currentValue.max_value,
              });
            }}
            disabled={disabled}
            type="number"
            placeholder={placeholders.min}
            sx={{
              maxWidth: "25%",
              backgroundColor: isChanged ? "rgba(25, 118, 210, 0.08)" : "#fff",
            }}
            InputProps={{ inputProps: { step: "any" } }}
          />
          <TextField
            size="small"
            variant="outlined"
            value={maxValue}
            onChange={(e) => {
              const nextMax =
                e.target.value === "" ? null : Number(e.target.value);
              onChange({
                min_value: currentValue.min_value,
                max_value:
                  nextMax === null || Number.isNaN(nextMax)
                    ? Number.NaN
                    : nextMax,
              });
            }}
            disabled={disabled}
            type="number"
            placeholder={placeholders.max}
            sx={{
              maxWidth: "25%",
              backgroundColor: isChanged ? "rgba(25, 118, 210, 0.08)" : "#fff",
            }}
            InputProps={{ inputProps: { step: "any" } }}
          />
        </Box>
      </FormControl>
    );
  }

  if (isPartNumberValue(currentValue)) {
    return (
      <FormControl
        fullWidth
        sx={{ display: "flex", flexDirection: "column", flex: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginLeft: "auto",
            maxWidth: "60%",
          }}
        >
          <TextField
            size="small"
            variant="outlined"
            value={currentValue.part_number || ""}
            onChange={(e) =>
              onChange({
                ...currentValue,
                part_number: e.target.value,
              })
            }
            disabled={disabled}
            placeholder={placeholders.text}
            sx={{
              backgroundColor: isChanged ? "rgba(25, 118, 210, 0.08)" : "#fff",
            }}
          />
          <Typography
            sx={{
              fontSize: "13px",
              color: "#666",
              whiteSpace: "nowrap",
              textDecoration: isChanged ? "line-through" : "none",
            }}
          >
            {currentValue.name || ""}
            {currentValue.description ? ` (${currentValue.description})` : ""}
            {currentValue.revision ? ` REV${currentValue.revision}` : ""}
          </Typography>
        </Box>
      </FormControl>
    );
  }

  const selectSx = {
    maxWidth: "50%",
    marginLeft: "auto",
    fontSize: "14px",
    backgroundColor: isChanged ? "rgba(25, 118, 210, 0.08)" : "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d0d0d0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#b0b0b0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1976d2",
    },
  };

  if (isMultiSelect && enumValues && enumValues.length > 0) {
    // Stored as a string[] in enum_values order. parseMultiEnumValue also
    // recovers legacy values saved as a single comma-joined string.
    const selectedValues = parseMultiEnumValue(currentValue, enumValues);
    return (
      <Select<string[]>
        multiple
        size="small"
        value={selectedValues}
        onChange={(e) => {
          onChange(
            serializeMultiEnum(
              parseMultiEnumValue(e.target.value, enumValues),
              enumValues
            )
          );
        }}
        disabled={disabled}
        fullWidth
        displayEmpty
        renderValue={(selected) =>
          selected.length === 0 ? (
            <Box component="span" sx={{ color: "#999" }}>
              {placeholders.select}
            </Box>
          ) : (
            <Box
              component="span"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {serializeMultiEnum(selected, enumValues).join(", ")}
            </Box>
          )
        }
        sx={{ ...selectSx, minWidth: 0 }}
      >
        {enumValues.map((value: string) => (
          <MenuItem key={value} value={value}>
            <Checkbox
              size="small"
              checked={selectedValues.includes(value)}
              sx={{ padding: "0 8px 0 0" }}
            />
            <ListItemText
              primary={value}
              primaryTypographyProps={{ fontSize: "14px" }}
            />
          </MenuItem>
        ))}
      </Select>
    );
  }

  if (enumValues && enumValues.length > 0) {
    return (
      <Select
        size="small"
        value={currentValue || ""}
        onChange={(e) => onChange(e.target.value as ValueType)}
        disabled={disabled}
        fullWidth
        displayEmpty
        renderValue={(selected) =>
          selected === "" || selected === null || selected === undefined ? (
            <Box component="span" sx={{ color: "#999" }}>
              {placeholders.select}
            </Box>
          ) : (
            String(selected)
          )
        }
        sx={selectSx}
      >
        {enumValues.map((value: string) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </Select>
    );
  }

  return (
    <FormControl
      error={violatesRange}
      fullWidth
      sx={{ display: "flex", flexDirection: "column", flex: 1 }}
    >
      <TextField
        size="small"
        variant="outlined"
        value={currentValue || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        type={rangeConstraint ? "number" : "text"}
        placeholder={placeholders.text}
        error={violatesRange}
        fullWidth
        sx={{
          maxWidth: "50%",
          marginLeft: "auto",
          "& .MuiInputBase-input": {
            fontSize: "14px",
            color: "#333",
          },
          backgroundColor: isChanged ? "rgba(25, 118, 210, 0.08)" : "#fff",
        }}
        InputProps={{
          ...(units
            ? {
                endAdornment: (
                  <InputAdornment position="end">{units}</InputAdornment>
                ),
              }
            : {}),
          sx: {
            "&.Mui-error": {
              borderColor: "#FB8C00 !important",
              border: "1px solid #FB8C00 !important",
            },
            "&.Mui-error:hover": {
              borderColor: "#FB8C00 !important",
              border: "1px solid #FB8C00 !important",
            },
            "&.Mui-error.Mui-focused": {
              borderColor: "#FB8C00 !important",
              border: "1px solid #FB8C00 !important",
            },
          },
        }}
      />
      {violatesRange && (
        <FormHelperText
          sx={{
            marginLeft: "auto",
            maxWidth: "50%",
            color: "#FB8C00 !important",
          }}
        >
          {validationMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// Distinguishes between editing a newly added row ("update"),
// versus editing a suggested row ("add")
const getEditOperation = (
  existingChange: Change | null,
  hasSavedValue: boolean
): Change["operation"] => {
  if (existingChange?.operation === "add") return "add";
  if (!existingChange && !hasSavedValue) return "add";
  return "update";
};

interface AttributeInputProps {
  item: ArenaOnlyItem | Item;
  attributeName: string;
  attributeValue: Value;
  disabled?: boolean;
  units?: string | null;
}

export const AttributeInput: React.FC<AttributeInputProps> = ({
  item,
  attributeName,
  attributeValue,
  disabled = false,
  units,
}) => {
  const { uuid } = getItemMetadata(item);
  const [searchParams] = useSearchParams();
  const currentPath = searchParams.get("bom-item") || "";

  // Create structured change target
  const changeTarget: ChangeTarget = useMemo(
    () => ({
      type: "attribute",
      path: currentPath,
      propertyName: attributeName,
    }),
    [currentPath, attributeName]
  );

  // Use direct store selectors to ensure re-renders on changes
  const trackChange = useSpecificationStateStore((state) => state.trackChange);
  const evaluateFormulas = useValidationStateStore(
    (state) => state.evaluateFormulas
  );

  // Subscribe to the change for this specific target - this will trigger re-renders
  const change = useSpecificationStateStore((state) => {
    const result = state.getChange(changeTarget);
    return result;
  });

  const isChanged = change !== null;
  const currentValue = change?.currentValue ?? attributeValue.value;

  // Get the attribute definition from item.item_type.attributes
  const attributeMetadata = useMemo(() => {
    if (!("item_type" in item) || !item.item_type) {
      return null;
    }
    return (
      item.item_type.attributes.find((attr) => attr.name === attributeName) ||
      null
    );
  }, [item, attributeName]);

  // Get constraints from store - subscribe to constraints directly to trigger recomputation
  const constraints = useValidationStateStore((state) => state.constraints);

  // Get every constraint on this attribute - will update when constraints load
  const attributeConstraints = useMemo(() => {
    if (!uuid || !attributeMetadata) return [];
    return getAttributeConstraints(constraints, uuid, attributeName);
    // eslint-disable-next-line
  }, [uuid, attributeName, attributeMetadata, constraints]);

  // Check if this attribute is locked
  const isLocked = useMemo(() => {
    return isFieldLocked(attributeConstraints);
  }, [attributeConstraints]);

  // The attribute's range constraint, if any
  const attributeRangeConstraint = useMemo(
    () => findConstraintOfType(attributeConstraints, "attribute_range"),
    [attributeConstraints]
  );

  // Get enum values if data_type is ENUM or MULTISELECT_ENUM
  const enumValues = useMemo(() => {
    if (!attributeMetadata || !isEnumDataType(attributeMetadata.data_type)) {
      return null;
    }
    const values = getEnumValuesFromConfig(attributeMetadata.config);
    return values.length > 0 ? values : null;
  }, [attributeMetadata]);

  const isMultiSelect = isMultiSelectEnumDataType(attributeMetadata?.data_type);

  // Get range constraint if applicable
  const rangeConstraint: { min?: number; max?: number } | null = useMemo(
    () => getRangeConstraintBounds(attributeRangeConstraint),
    [attributeRangeConstraint]
  );

  const handleRangeValueChange = (
    nextValue: RangeValue,
    previousValue: RangeValue | null
  ) => {
    const operation = getEditOperation(
      change,
      attributeName in (item.attributes || {})
    );
    const rangeChange: Change = {
      target: changeTarget,
      operation,
      currentValue: nextValue,
      // Added fields have no saved baseline to diff against.
      previousValue:
        operation === "add" ? undefined : previousValue ?? undefined,
      timestamp: Date.now(),
    };
    trackChange(rangeChange);
    evaluateFormulas(changeTarget);
  };

  // Local state for violation display
  const [violatesRange, setViolatesRange] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );

  // Validate range constraints whenever currentValue changes
  useEffect(() => {
    if (attributeRangeConstraint && rangeConstraint) {
      validateRangeConstraint(
        currentValue as string | number | boolean | null | undefined,
        rangeConstraint,
        setViolatesRange,
        setValidationMessage,
        "attribute_range",
        attributeName
      );
    }
    // eslint-disable-next-line
  }, [currentValue, attributeRangeConstraint, rangeConstraint, currentPath]);
  return (
    <ValueEditor
      currentValue={currentValue as ValueType}
      isChanged={isChanged}
      isLocked={isLocked}
      disabled={disabled}
      enumValues={enumValues}
      isMultiSelect={isMultiSelect}
      rangeConstraint={rangeConstraint}
      violatesRange={violatesRange}
      validationMessage={validationMessage}
      units={units}
      placeholderValue={attributeMetadata?.default ?? null}
      onChange={(nextValue) => {
        if (isRangeValue(nextValue)) {
          handleRangeValueChange(
            nextValue,
            isRangeValue(currentValue) ? currentValue : null
          );
          return;
        }
        const operation = getEditOperation(
          change,
          attributeName in (item.attributes || {})
        );
        const valueChange: Change = {
          target: changeTarget,
          operation,
          currentValue: nextValue,
          previousValue: operation === "add" ? undefined : currentValue,
          timestamp: Date.now(),
        };
        trackChange(valueChange);
        evaluateFormulas(changeTarget);
      }}
    />
  );
};

interface ParameterInputProps {
  item: ArenaOnlyItem | Item;
  process: Process;
  processIndex: number;
  /** Full path for nested steps; defaults to `[processIndex]`. */
  processPath?: number[];
  parameterName: string;
  parameterValue: Value;
  disabled?: boolean;
  units?: string | null;
}

export const ParameterInput: React.FC<ParameterInputProps> = ({
  process,
  processIndex,
  processPath,
  parameterName,
  parameterValue,
  disabled = false,
  units,
}) => {
  const [searchParams] = useSearchParams();
  const currentPath = searchParams.get("bom-item") || "";

  const processPathKey = (processPath ?? [processIndex]).join(">");

  // Create structured change target
  const changeTarget: ChangeTarget = useMemo(
    () => ({
      type: "parameter",
      path: currentPath,
      processPath: processPathKey.split(">").map(Number),
      propertyName: parameterName,
    }),
    [currentPath, processPathKey, parameterName]
  );

  // Use direct store selectors to ensure re-renders on changes
  const trackChange = useSpecificationStateStore((state) => state.trackChange);

  // Subscribe to the change for this specific target - this will trigger re-renders
  const change = useSpecificationStateStore((state) => {
    const result = state.getChange(changeTarget);
    return result;
  });

  const isChanged = change !== null;
  const currentValue = change?.currentValue ?? parameterValue.value;

  // Get the parameter definition from process.process_type.parameters
  const parameterMetadata = useMemo(() => {
    if (!process.process_type || !process.process_type.parameters) {
      return null;
    }
    return (
      process.process_type.parameters.find(
        (param) => param.name === parameterName
      ) || null
    );
  }, [process, parameterName]);

  // Get constraints from store - subscribe to constraints directly to trigger recomputation
  const constraints = useValidationStateStore((state) => state.constraints);

  const parameterConstraints = useMemo(() => {
    if (!process.uuid || !parameterMetadata) return [];
    return getParameterConstraints(constraints, process.uuid, parameterName);
    // eslint-disable-next-line
  }, [process.uuid, parameterName, parameterMetadata, constraints]);

  // Check if this parameter is locked
  const isLocked = useMemo(() => {
    return isFieldLocked(parameterConstraints);
  }, [parameterConstraints]);

  // The parameter's range constraint, if any
  const parameterRangeConstraint = useMemo(
    () => findConstraintOfType(parameterConstraints, "parameter_range"),
    [parameterConstraints]
  );

  // Get enum values if data_type is ENUM or MULTISELECT_ENUM
  const enumValues = useMemo(() => {
    if (!parameterMetadata || !isEnumDataType(parameterMetadata.data_type)) {
      return null;
    }
    const values = getEnumValuesFromConfig(parameterMetadata.config);
    return values.length > 0 ? values : null;
  }, [parameterMetadata]);

  const isMultiSelect = isMultiSelectEnumDataType(parameterMetadata?.data_type);

  // Get range constraint if applicable
  const rangeConstraint: { min?: number; max?: number } | null = useMemo(
    () => getRangeConstraintBounds(parameterRangeConstraint),
    [parameterRangeConstraint]
  );

  const handleRangeValueChange = (
    nextValue: RangeValue,
    previousValue: RangeValue | null
  ) => {
    const operation = getEditOperation(
      change,
      parameterName in (process.parameters || {})
    );
    const rangeChange: Change = {
      target: changeTarget,
      operation,
      currentValue: nextValue,
      // Added fields have no saved baseline to diff against.
      previousValue:
        operation === "add" ? undefined : previousValue ?? undefined,
      timestamp: Date.now(),
    };
    trackChange(rangeChange);
  };

  // Local state for violation display
  const [violatesRange, setViolatesRange] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );

  // Validate range constraints whenever currentValue changes
  useEffect(() => {
    if (parameterRangeConstraint && rangeConstraint) {
      validateRangeConstraint(
        currentValue as string | number | boolean | null | undefined,
        rangeConstraint,
        setViolatesRange,
        setValidationMessage,
        "parameter_range",
        parameterName
      );
    }
    // eslint-disable-next-line
  }, [currentValue, parameterRangeConstraint, rangeConstraint, currentPath]);
  return (
    <ValueEditor
      currentValue={currentValue as ValueType}
      isChanged={isChanged}
      isLocked={isLocked}
      disabled={disabled}
      enumValues={enumValues}
      isMultiSelect={isMultiSelect}
      rangeConstraint={rangeConstraint}
      violatesRange={violatesRange}
      validationMessage={validationMessage}
      units={units}
      placeholderValue={parameterMetadata?.default ?? null}
      onChange={(nextValue) => {
        if (isRangeValue(nextValue)) {
          handleRangeValueChange(
            nextValue,
            isRangeValue(currentValue) ? currentValue : null
          );
          return;
        }
        const operation = getEditOperation(
          change,
          parameterName in (process.parameters || {})
        );
        const valueChange: Change = {
          target: changeTarget,
          operation,
          currentValue: nextValue,
          previousValue: operation === "add" ? undefined : currentValue,
          timestamp: Date.now(),
        };
        trackChange(valueChange);
      }}
    />
  );
};
